/*
 * Preview — Prebook & Deposit Flow
 *
 * Visual walkthrough of the proposed prebook architecture. No backend calls,
 * no DB changes — pure UI mockup using mock data. Hit
 *     /preview/prebook-flow
 * to view.
 *
 * 5 screens via top tabs:
 *   1. Seller setting — "Offer prebook" toggle
 *   2. Seller's PO review — accept-as-is OR convert-to-prebook
 *   3. Buyer's acceptance screen — pay deposit, order becomes active
 *   4. Overdue + extension request flow (both sides)
 *   5. Expanded Claims section — deposit disputes pre-delivery
 */
import { useState } from "react";
import {
  CheckCircle2, Clock, AlertTriangle, ArrowRight, X, Calendar,
  DollarSign, ShieldCheck, FileText, ChevronRight, Info, Zap,
  PackageCheck, MessageSquare, RotateCcw, ThumbsUp, ThumbsDown,
} from "lucide-react";

const TABS = [
  { id: "setting",   num: 1, label: "Seller setting" },
  { id: "review",    num: 2, label: "Seller reviews PO" },
  { id: "accept",    num: 3, label: "Buyer accepts" },
  { id: "overdue",   num: 4, label: "Overdue + extension" },
  { id: "claims",    num: 5, label: "Claims expansion" },
];

export default function PrebookFlowPreview() {
  const [tab, setTab] = useState("setting");
  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Header */}
      <div className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-[11px] mono uppercase tracking-widest text-foreground/45">Preview — visual mockup only</div>
            <div className="display text-2xl">Prebook & Deposit flow</div>
          </div>
          <div className="text-[11.5px] text-foreground/55 max-w-md text-right">
            No production code touched. Click through the 5 screens to validate the design before we ship it.
          </div>
        </div>
        {/* Tab strip */}
        <div className="max-w-6xl mx-auto px-6 pb-3 flex flex-wrap gap-2">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`text-[12.5px] inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 transition-colors ${
                tab === t.id ? "bg-foreground text-background border-foreground"
                             : "bg-white text-foreground/75 border-border hover:border-foreground/40"
              }`}
            >
              <span className="mono text-[10.5px] opacity-60">{t.num}</span>{t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {tab === "setting" && <SettingScreen />}
        {tab === "review"  && <ReviewScreen />}
        {tab === "accept"  && <AcceptScreen />}
        {tab === "overdue" && <OverdueScreen />}
        {tab === "claims"  && <ClaimsScreen />}
      </div>
    </div>
  );
}

/* ── Reusable annotation strip — explains what's on each screen ───────── */
function Annotation({ title, points }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4 mb-5">
      <div className="text-[10.5px] mono uppercase tracking-widest text-foreground/45 mb-1.5">What this is</div>
      <div className="text-[14px] font-medium mb-2">{title}</div>
      <ul className="space-y-1 text-[12.5px] text-foreground/70">
        {points.map((p, i) => (
          <li key={i} className="flex gap-2"><span className="mono text-foreground/40">→</span>{p}</li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Screen 1 — Seller setting
   ───────────────────────────────────────────────────────────────────────── */
function SettingScreen() {
  const [on, setOn] = useState(false);
  return (
    <>
      <Annotation
        title="In Seller → Settings → Public profile → Logistics"
        points={[
          "Single toggle: 'Offer prebook (deposit + balance flow)'. Default OFF.",
          "When OFF: buyers never see any deposit option during ordering — pure stock-on-hand flow.",
          "When ON: seller can convert any incoming PO into a prebook with custom deposit % + lead time.",
        ]}
      />
      <div className="rounded-2xl border border-border bg-white p-6 max-w-3xl">
        <div className="text-[11px] mono uppercase tracking-widest text-foreground/45">Logistics & geography</div>
        <div className="mt-1 mb-5 text-[13px] text-foreground/60">Where you ship from, where you ship to, and how you take orders.</div>

        <div className="space-y-4 divide-y divide-border/60">
          <Field label="Ships from" hint="City, state, country.">
            <input className="h-10 rounded-md border border-border px-3 text-sm w-full max-w-sm" defaultValue="Los Angeles, CA, USA" disabled/>
          </Field>
          <Field label="Restocking fee (%)" hint="Already shipping — applies to packed-then-cancelled orders.">
            <div className="inline-flex items-center gap-2">
              <input className="h-10 rounded-md border border-border px-3 text-sm w-24 mono" defaultValue="10" disabled/>
              <span className="text-[12.5px] text-foreground/60">% of order total</span>
            </div>
          </Field>
          <Field
            label="Offer prebook"
            hint="Take pre-orders for made-to-order goods with a deposit upfront and balance when ready. You set the deposit % and lead time per PO."
          >
            <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
              <button
                onClick={() => setOn(v => !v)}
                className={`relative w-11 h-6 rounded-full transition-colors ${on ? "bg-foreground" : "bg-foreground/15"}`}
                aria-checked={on}
                role="switch"
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : ""}`} />
              </button>
              <span className="text-[13.5px] text-foreground/85">
                {on
                  ? "Yes — I take pre-orders and can request a deposit per PO"
                  : "No — stock-on-hand orders only"}
              </span>
            </label>
            {on && (
              <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[12px] text-emerald-900 max-w-md">
                Prebook turned on. Every new PO you receive will show a <strong>"Convert to prebook"</strong> button alongside the regular Accept button. You decide which orders need a deposit.
              </div>
            )}
          </Field>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Screen 2 — Seller reviews PO (accept-as-is OR convert-to-prebook)
   ───────────────────────────────────────────────────────────────────────── */
function ReviewScreen() {
  const [mode, setMode] = useState("idle"); // idle | prebook
  const [depositPct, setDepositPct] = useState(30);
  const [leadWeeks, setLeadWeeks] = useState(8);
  const total = 12450;
  const deposit = Math.round(total * depositPct / 100);
  const balance = total - deposit;
  const promised = new Date(Date.now() + leadWeeks * 7 * 86400e3).toLocaleDateString("en-US", { dateStyle: "medium" });

  return (
    <>
      <Annotation
        title="On the seller's order detail (when prebook is enabled)"
        points={[
          "Buyer submitted PO normally — no deposit fields shown to them on submit.",
          "Seller sees two action buttons: 'Accept as-is' (normal flow) and 'Convert to prebook'.",
          "Converting opens a small form: deposit % + worst-case lead time (weeks). Seller commits to that lead time when buyer pays deposit.",
        ]}
      />
      <div className="rounded-2xl border border-border bg-white p-6 max-w-3xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[11px] mono uppercase tracking-widest text-foreground/45">Incoming PO</div>
            <div className="display text-xl">ORD-PO-9412</div>
            <div className="text-[12px] text-foreground/55 mt-0.5">From Apex Wholesale · 3 line items</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] mono uppercase tracking-widest text-foreground/45">Order total</div>
            <div className="display text-2xl mono">${total.toLocaleString()}</div>
          </div>
        </div>

        <div className="rounded-xl border border-border divide-y divide-border mb-5">
          {[
            ["Hydration Serum 30ml", 200, 18],
            ["Glow Mist 100ml",      150, 24],
            ["Renew Cream 50g",      120, 35],
          ].map(([name, qty, price]) => (
            <div key={name} className="flex justify-between p-3 text-[13px]">
              <div>
                <div className="font-medium">{name}</div>
                <div className="text-[11.5px] text-foreground/55">{qty} units × ${price}</div>
              </div>
              <div className="mono">${(qty * price).toLocaleString()}</div>
            </div>
          ))}
        </div>

        {mode === "idle" && (
          <div className="flex gap-2">
            <button className="rounded-full bg-foreground text-background text-[13px] px-5 py-2.5 hover:opacity-90 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4"/> Accept as-is &amp; invoice
            </button>
            <button
              onClick={() => setMode("prebook")}
              className="rounded-full bg-amber-500 text-white text-[13px] px-5 py-2.5 hover:bg-amber-600 inline-flex items-center gap-1.5"
            >
              <Clock className="w-4 h-4"/> Convert to prebook
            </button>
            <div className="ml-auto text-[11.5px] text-foreground/50 self-center">
              <Info className="w-3 h-3 inline mr-1 -mt-0.5"/>
              "Convert" is only visible because prebook is ON in your settings.
            </div>
          </div>
        )}

        {mode === "prebook" && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 space-y-4">
            <div className="text-[13px] font-medium text-amber-900">Convert this PO to a prebook</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11.5px] text-amber-900 mb-1 block">Deposit %</label>
                <div className="inline-flex items-center gap-2">
                  <input
                    type="number" min="0" max="100" step="5"
                    value={depositPct}
                    onChange={e => setDepositPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                    className="h-10 rounded-md border border-amber-300 bg-white px-3 text-sm mono w-24"
                  />
                  <span className="text-[12.5px] text-amber-900">% of total</span>
                </div>
              </div>
              <div>
                <label className="text-[11.5px] text-amber-900 mb-1 block">Worst-case lead time (weeks)</label>
                <div className="inline-flex items-center gap-2">
                  <input
                    type="number" min="1" max="52"
                    value={leadWeeks}
                    onChange={e => setLeadWeeks(Math.max(1, Math.min(52, Number(e.target.value) || 1)))}
                    className="h-10 rounded-md border border-amber-300 bg-white px-3 text-sm mono w-24"
                  />
                  <span className="text-[12.5px] text-amber-900">weeks</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-amber-200 bg-white p-4 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-[10.5px] mono uppercase tracking-widest text-foreground/50">Deposit now</div>
                <div className="display text-lg mono">${deposit.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10.5px] mono uppercase tracking-widest text-foreground/50">Balance later</div>
                <div className="display text-lg mono">${balance.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10.5px] mono uppercase tracking-widest text-foreground/50">Promised by</div>
                <div className="display text-lg">{promised}</div>
              </div>
            </div>

            <div className="text-[11.5px] text-amber-900/85 leading-snug">
              <ShieldCheck className="w-3.5 h-3.5 inline mr-1 -mt-0.5"/>
              <strong>Heads-up:</strong> Once the buyer pays the deposit, you're committed to the <strong>{promised}</strong> date.
              If you miss it, the buyer can cancel and get an instant deposit refund. You can request up to <strong>3 extensions</strong>,
              each requiring the buyer's explicit approval.
            </div>

            <div className="flex gap-2 justify-end">
              <button onClick={() => setMode("idle")} className="text-[12.5px] rounded-full border border-amber-300 px-4 py-2 text-amber-900 hover:bg-amber-100">Back</button>
              <button className="text-[12.5px] rounded-full bg-foreground text-background px-5 py-2 hover:opacity-90 inline-flex items-center gap-1">
                Send prebook offer to buyer <ArrowRight className="w-3.5 h-3.5"/>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Screen 3 — Buyer accepts the prebook & pays deposit
   ───────────────────────────────────────────────────────────────────────── */
function AcceptScreen() {
  const [state, setState] = useState("offer"); // offer | active
  return (
    <>
      <Annotation
        title="On the buyer's order detail — seller has converted the PO to a prebook"
        points={[
          "Buyer sees: deposit due now, balance later, promised ready-by date, and the production commitment.",
          "Two buttons: Pay deposit (becomes active) or Decline (cancels at no cost).",
          "Once deposit paid, order shows live production status + countdown to promised date.",
        ]}
      />

      {state === "offer" && (
        <div className="rounded-2xl border border-border bg-white p-6 max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10.5px] mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">Prebook offer</span>
            <span className="text-[11px] text-foreground/55">from GlowCo Beauty · awaiting your action</span>
          </div>
          <div className="display text-xl mb-1">ORD-PO-9412</div>
          <div className="text-[12.5px] text-foreground/60 mb-5">GlowCo proposes to take this as a prebook — deposit now to start production, balance when ready.</div>

          <div className="rounded-xl border border-border p-4 bg-secondary/30 grid grid-cols-3 gap-3 text-center mb-4">
            <div><div className="text-[10.5px] mono uppercase tracking-widest text-foreground/50">Deposit now</div><div className="display text-xl mono">$3,735</div><div className="text-[10.5px] text-foreground/50 mt-0.5">30% of order</div></div>
            <div><div className="text-[10.5px] mono uppercase tracking-widest text-foreground/50">Balance later</div><div className="display text-xl mono">$8,715</div><div className="text-[10.5px] text-foreground/50 mt-0.5">due when ready</div></div>
            <div><div className="text-[10.5px] mono uppercase tracking-widest text-foreground/50">Ready by</div><div className="display text-xl">Aug 12</div><div className="text-[10.5px] text-foreground/50 mt-0.5">8 weeks</div></div>
          </div>

          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[12px] text-emerald-900 leading-snug mb-4">
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1 -mt-0.5"/>
            <strong>Your protection:</strong> If GlowCo doesn't have your order ready by <strong>Aug 12</strong>, you can cancel with one click and your deposit is auto-refunded. GlowCo can request up to 3 extensions — each one needs your explicit acceptance.
          </div>

          <div className="flex gap-2 justify-end">
            <button className="text-[12.5px] rounded-full border border-border px-4 py-2 hover:bg-secondary/50">Decline (no fee)</button>
            <button onClick={() => setState("active")} className="text-[12.5px] rounded-full bg-foreground text-background px-5 py-2 hover:opacity-90 inline-flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5"/> Pay $3,735 deposit &amp; activate
            </button>
          </div>
        </div>
      )}

      {state === "active" && (
        <div className="rounded-2xl border border-border bg-white p-6 max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10.5px] mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-200">Prebook active</span>
            <span className="text-[11px] text-foreground/55">deposit secured · production in progress</span>
          </div>
          <div className="display text-xl mb-4">ORD-PO-9412</div>

          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="rounded-xl border border-border p-3"><div className="text-[10.5px] mono uppercase tracking-widest text-foreground/50">Deposit paid</div><div className="display text-lg mono text-emerald-700">$3,735</div></div>
            <div className="rounded-xl border border-border p-3"><div className="text-[10.5px] mono uppercase tracking-widest text-foreground/50">Balance pending</div><div className="display text-lg mono">$8,715</div></div>
            <div className="rounded-xl border border-border p-3"><div className="text-[10.5px] mono uppercase tracking-widest text-foreground/50">Ready by</div><div className="display text-lg">Aug 12</div><div className="text-[10.5px] text-foreground/50">in 56 days</div></div>
          </div>

          <div className="rounded-xl border border-border p-4 mb-3">
            <div className="text-[11px] mono uppercase tracking-widest text-foreground/45 mb-3">Production status</div>
            <ol className="space-y-2.5">
              {[
                ["Deposit secured",          "Today",        true],
                ["Materials sourced",        "Pending",      false],
                ["Production 50%",           "Pending",      false],
                ["QC passed · ready",        "Pending",      false],
              ].map(([label, when, done], i) => (
                <li key={i} className="flex items-center gap-2.5 text-[12.5px]">
                  {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/> : <span className="w-4 h-4 rounded-full border border-foreground/20"/>}
                  <span className={done ? "font-medium" : "text-foreground/55"}>{label}</span>
                  <span className="ml-auto text-foreground/45 text-[11.5px]">{when}</span>
                </li>
              ))}
            </ol>
          </div>
          <button onClick={() => setState("offer")} className="text-[11.5px] text-foreground/55 hover:underline">← back to offer view</button>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Screen 4 — Overdue + extension request flow
   ───────────────────────────────────────────────────────────────────────── */
function OverdueScreen() {
  const [view, setView] = useState("buyer"); // buyer | seller
  return (
    <>
      <Annotation
        title="What happens when promised_ready_by has passed and seller isn't ready"
        points={[
          "Buyer view: order is flagged overdue. Two prominent buttons — 'Cancel + auto-refund' OR (if seller filed one) 'Accept seller's extension'.",
          "Seller view: 'Request extension' button. Specify new ETA + reason; goes to buyer for accept/reject. Max 3 extensions per order.",
          "If buyer doesn't respond to extension within 7 days → treated as no answer; cancel button stays available.",
          "If buyer rejects extension OR seller hits 3-extension cap → one-click cancel & full deposit refund.",
        ]}
      />

      {/* View toggle */}
      <div className="inline-flex rounded-full border border-border p-1 mb-4 bg-white">
        {["buyer", "seller"].map(v => (
          <button key={v} onClick={() => setView(v)}
                  className={`text-[12px] px-4 py-1.5 rounded-full ${view === v ? "bg-foreground text-background" : "text-foreground/65"}`}>
            {v === "buyer" ? "Buyer view" : "Seller view"}
          </button>
        ))}
      </div>

      {view === "buyer" && (
        <div className="rounded-2xl border border-border bg-white p-6 max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10.5px] mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-900 border border-red-200">Overdue · 5 days</span>
            <span className="text-[11px] text-foreground/55">promised Aug 12 · still not ready</span>
          </div>
          <div className="display text-xl mb-4">ORD-PO-9412 · GlowCo Beauty</div>

          {/* Seller's extension request inline */}
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-4">
            <div className="flex items-start gap-3">
              <MessageSquare className="w-4 h-4 text-amber-700 mt-0.5"/>
              <div className="flex-1">
                <div className="text-[12px] font-medium text-amber-900">GlowCo has requested an extension <span className="mono text-amber-700">(1 of 3)</span></div>
                <div className="text-[12px] text-amber-900/85 mt-1">New ETA: <strong>Aug 26</strong> (+14 days)</div>
                <div className="text-[11.5px] text-amber-900/70 mt-1">"Bottle supplier slipped. New shipment confirmed Aug 22, can pack same day."</div>
                <div className="flex gap-2 mt-3">
                  <button className="text-[11.5px] rounded-full bg-foreground text-background px-3.5 py-1.5 inline-flex items-center gap-1 hover:opacity-90">
                    <ThumbsUp className="w-3.5 h-3.5"/> Accept new date
                  </button>
                  <button className="text-[11.5px] rounded-full border border-amber-300 text-amber-900 px-3.5 py-1.5 inline-flex items-center gap-1 hover:bg-amber-100">
                    <ThumbsDown className="w-3.5 h-3.5"/> Reject
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="text-[11.5px] mono uppercase tracking-widest text-foreground/45 mb-2">Your options</div>
          <div className="grid sm:grid-cols-2 gap-2 mb-3">
            <button className="rounded-xl border border-red-200 bg-red-50 p-3.5 text-left hover:border-red-400">
              <div className="flex items-center gap-2"><X className="w-4 h-4 text-red-700"/><span className="text-[13px] font-medium text-red-900">Cancel order</span></div>
              <div className="text-[11.5px] text-red-900/80 mt-1">Get your $3,735 deposit refunded instantly. Seller forfeits the prebook.</div>
            </button>
            <button className="rounded-xl border border-border bg-white p-3.5 text-left hover:border-foreground/40">
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-foreground/70"/><span className="text-[13px] font-medium">Wait for seller</span></div>
              <div className="text-[11.5px] text-foreground/65 mt-1">Hold off on cancelling — they can still request extensions you approve.</div>
            </button>
          </div>

          <div className="text-[11px] text-foreground/55 leading-snug">
            <Info className="w-3 h-3 inline mr-1 -mt-0.5"/>
            Already cancelled and the seller disputes it? Open a <strong>deposit dispute claim</strong> from the Claims tab — TRAQO will mediate.
          </div>
        </div>
      )}

      {view === "seller" && (
        <div className="rounded-2xl border border-border bg-white p-6 max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10.5px] mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-900 border border-red-200">Overdue · 5 days</span>
            <span className="text-[11px] text-foreground/55">your promised date has passed</span>
          </div>
          <div className="display text-xl mb-4">ORD-PO-9412 · Apex Wholesale</div>

          <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-4 text-[12.5px] text-red-900">
            <AlertTriangle className="w-4 h-4 inline mr-1 -mt-0.5"/>
            <strong>The buyer can cancel and get an instant refund any time now.</strong> Request an extension to keep the order alive — they'll see it immediately. You have <strong>3 extension requests</strong> per order; you've used <strong>0</strong>.
          </div>

          <div className="rounded-xl border border-border p-4 mb-3 space-y-3">
            <div className="text-[11px] mono uppercase tracking-widest text-foreground/45">Request an extension</div>
            <div>
              <label className="text-[11.5px] text-foreground/60 mb-1 block">New promised date</label>
              <input type="date" defaultValue="2026-08-26" className="h-10 rounded-md border border-border px-3 text-sm mono"/>
            </div>
            <div>
              <label className="text-[11.5px] text-foreground/60 mb-1 block">Reason (shown to buyer)</label>
              <textarea
                rows={2}
                defaultValue="Bottle supplier slipped. New shipment confirmed Aug 22, can pack same day."
                className="w-full rounded-md border border-border px-3 py-2 text-[12.5px]"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button className="text-[12px] rounded-full border border-border px-4 py-2 text-foreground/70">Cancel</button>
              <button className="text-[12px] rounded-full bg-foreground text-background px-4 py-2 hover:opacity-90 inline-flex items-center gap-1.5">
                <RotateCcw className="w-3.5 h-3.5"/> Send extension request
              </button>
            </div>
          </div>

          <div className="text-[11px] text-foreground/55 leading-snug">
            <Info className="w-3 h-3 inline mr-1 -mt-0.5"/>
            Buyer rejects or hits the 3-extension cap → order auto-cancels + deposit refunds. Want to dispute? File a claim from Order detail.
          </div>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Screen 5 — Claims section expanded to include pre-delivery deposit cases
   ───────────────────────────────────────────────────────────────────────── */
function ClaimsScreen() {
  return (
    <>
      <Annotation
        title="Claims tab now accepts pre-delivery cases — not just post-delivery damage"
        points={[
          "New 'Order stage' filter at the top: Pre-delivery deposit disputes · In-transit / damage · Post-delivery quality.",
          "Buyer can file: Lead-time slip dispute · Deposit refund delay · Wrong prebook terms.",
          "Seller can file: Bad-faith cancellation · Buyer non-response to extension.",
          "TRAQO mediates with timeline + evidence (chat history, extension log, deposit receipts) auto-attached.",
        ]}
      />

      <div className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-[11px] mono uppercase tracking-widest text-foreground/45">Claims &amp; disputes</div>
            <div className="display text-xl">All cases (4)</div>
          </div>
          <button className="rounded-full bg-foreground text-background text-[12.5px] px-4 py-2 inline-flex items-center gap-1.5 hover:opacity-90">
            <FileText className="w-3.5 h-3.5"/> File a new claim
          </button>
        </div>

        {/* Stage filter chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            "All",
            "Pre-delivery (deposits / lead-time)",
            "In-transit (damage / loss)",
            "Post-delivery (quality / wrong item)",
          ].map((label, i) => (
            <button key={i}
                    className={`text-[11.5px] rounded-full border px-3 py-1.5 ${
                      i === 0 ? "bg-foreground text-background border-foreground" : "bg-white text-foreground/70 border-border hover:border-foreground/40"
                    }`}>
              {label}
            </button>
          ))}
        </div>

        {/* Cases */}
        <div className="space-y-2">
          <ClaimRow
            stage="Pre-delivery" stageColor="amber"
            code="CLM-9412"
            title="Deposit not refunded after cancel"
            sub="Buyer cancelled ORD-PO-9412 (overdue prebook). $3,735 deposit pending refund 4 days ago."
            updated="2h ago" status="awaiting_seller"
          />
          <ClaimRow
            stage="Pre-delivery" stageColor="amber"
            code="CLM-9388"
            title="Lead-time slip dispute"
            sub="Seller missed promised date Aug 12; buyer rejected 2 extension requests. Awaiting TRAQO mediation."
            updated="1d ago" status="mediating"
          />
          <ClaimRow
            stage="In-transit" stageColor="indigo"
            code="CLM-9355"
            title="Damaged in transit"
            sub="2 of 4 cartons crushed on arrival. Photos + carrier POD attached."
            updated="3d ago" status="approved_refund"
          />
          <ClaimRow
            stage="Post-delivery" stageColor="emerald"
            code="CLM-9301"
            title="Wrong item received"
            sub="Buyer received Renew Cream 30g, ordered 50g. Replacement shipped."
            updated="6d ago" status="resolved"
          />
        </div>
      </div>
    </>
  );
}

function ClaimRow({ stage, stageColor, code, title, sub, updated, status }) {
  const stageBg = {
    amber:   "bg-amber-100 text-amber-900 border-amber-200",
    indigo:  "bg-indigo-100 text-indigo-900 border-indigo-200",
    emerald: "bg-emerald-100 text-emerald-900 border-emerald-200",
  }[stageColor];
  const statusLabel = {
    awaiting_seller:  ["Awaiting seller response",  "bg-amber-50 text-amber-800 border-amber-200"],
    mediating:        ["TRAQO mediating",           "bg-indigo-50 text-indigo-800 border-indigo-200"],
    approved_refund:  ["Refund approved",           "bg-emerald-50 text-emerald-800 border-emerald-200"],
    resolved:         ["Resolved",                  "bg-foreground/10 text-foreground/80 border-foreground/15"],
  }[status];
  return (
    <div className="rounded-xl border border-border bg-white p-4 flex items-start gap-3 hover:border-foreground/30 cursor-pointer">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`text-[10px] mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${stageBg}`}>{stage}</span>
          <span className="text-[10.5px] mono text-foreground/50">{code}</span>
          <span className={`text-[10px] mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${statusLabel[1]}`}>{statusLabel[0]}</span>
        </div>
        <div className="text-[13.5px] font-medium">{title}</div>
        <div className="text-[12px] text-foreground/65 mt-0.5 leading-snug">{sub}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[10.5px] text-foreground/50">Updated</div>
        <div className="text-[11.5px] mono">{updated}</div>
        <ChevronRight className="w-4 h-4 text-foreground/40 ml-auto mt-1"/>
      </div>
    </div>
  );
}

/* ── Tiny FieldRow helper used by Screen 1 ────────────────────────────── */
function Field({ label, hint, children }) {
  return (
    <div className="pt-4 first:pt-0">
      <div className="grid sm:grid-cols-[200px_1fr] gap-3">
        <div>
          <div className="text-[13px] font-medium">{label}</div>
          <div className="text-[11.5px] text-foreground/55 mt-0.5 leading-snug">{hint}</div>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
}
